import React, { useEffect, useState } from 'react';
import { presenzeAPI } from '../../services/personaleApi';

/**
 * «Le mie timbrature» (Alex, 28/10/2026): le proprie timbrature, giorno per
 * giorno, in sola lettura. Le coppie entrata-uscita, il totale del giorno, la
 * giornata corretta (e da chi, se veniva da una richiesta approvata), le
 * richieste di correzione in attesa.
 *
 * Tutto lo decide il server (GET /api/presenze/timbrature/mie): le coppie in
 * ora italiana, il totale - lo stesso di «Le mie ore» -, quali mesi si possono
 * vedere. Qui non si ricalcola niente. Il mese sta nell'indirizzo (lo tiene la
 * pagina): una ricarica lo ritrova.
 */

const MESI = ['', 'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
              'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const nomeMese = (m) => {
    const [a, n] = String(m || '').split('-').map(Number);
    return MESI[n] ? `${MESI[n]} ${a}` : String(m || '');
};
const giornoIta = (g) => {
    const x = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(g || ''));
    return x ? `${x[3]}/${x[2]}/${x[1]}` : String(g || '');
};
const ore = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n) || n === 0) return '—';
    return n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
};
const testoCorretto = (c) => {
    if (c.da_richiesta) return `corretto · approvato da ${c.approvata_da || '—'}`;
    return 'corretto dall\'amministrazione';
};

export default function MieTimbrature({ mese, onMese }) {
    const [dati, setDati] = useState(null);
    const [errore, setErrore] = useState(null);

    useEffect(() => {
        let vivo = true;
        setErrore(null);
        presenzeAPI.mieTimbrature(mese || undefined)
            .then((r) => { if (vivo) setDati(r.data.data); })
            .catch((e) => {
                if (!vivo) return;
                setDati(null);
                setErrore(e?.response?.data?.message || 'Impossibile caricare le timbrature');
            });
        return () => { vivo = false; };
    }, [mese]);

    if (errore) return <p className="adp-vuoto" data-mie-timbrature-errore>{errore}</p>;
    if (!dati) return <p className="adp-vuoto">Caricamento…</p>;

    return (
        <div data-mie-timbrature>
            <div className="adp-periodo">
                <label>
                    <span>Mese</span>
                    <select value={dati.mese} data-non-trattiene data-mese-timbrature
                            onChange={(e) => onMese(e.target.value)}>
                        {dati.mesi.map((m) => (
                            <option key={m} value={m}>{nomeMese(m)}</option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="adp-riepilogo">
                <div><b>{ore(dati.ore_totali)}</b><span>ore nel mese</span></div>
                <div><b>{dati.giorni.filter((g) => g.coppie.length).length}</b>
                     <span>giorni con timbrature</span></div>
            </div>
            {dati.giorni.length === 0 ? (
                <p className="adp-vuoto">Nessuna timbratura in {nomeMese(dati.mese)}.</p>
            ) : (
                <table className="adp-tabella">
                    <thead>
                        <tr>
                            <th>Giorno</th><th>Entrata – uscita</th>
                            <th className="num">Ore</th><th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dati.giorni.map((g) => (
                            <tr key={g.giorno} data-giorno={g.giorno}
                                className={g.domenica ? 'domenica' : ''}>
                                <td>{giornoIta(g.giorno)}{g.festa ? ` · ${g.festa}` : ''}</td>
                                <td data-coppie>
                                    {g.coppie.length ? g.coppie.map((c, i) => (
                                        <div key={i} data-coppia>
                                            {c.entrata || '—'} – {c.uscita || (c.in_servizio ? 'in servizio' : '—')}
                                        </div>
                                    )) : '—'}
                                </td>
                                <td className="num">{ore(g.ore)}</td>
                                <td>
                                    {g.sigla && <span>{g.sigla} </span>}
                                    {g.spaiate > 0 && (
                                        <span className="adp-pillola guasto">manca una timbratura</span>
                                    )}
                                    {g.corretto && (
                                        <span className="adp-pillola correzione" data-corretto>
                                            {testoCorretto(g.corretto)}
                                        </span>
                                    )}
                                    {g.in_attesa > 0 && (
                                        <span className="adp-pillola attesa" data-in-attesa>
                                            in attesa
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <p className="adp-nota">
                Qui le timbrature si leggono e basta. Se un orario è sbagliato o ne manca
                uno, chiedi la correzione da «Le mie ore»: finché l&apos;amministrazione
                non decide, il giorno resta segnato «in attesa».
            </p>
        </div>
    );
}
